import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import type { LinkedInConnectionStatus } from '@/api/user';

interface LinkedInConnectionProps {
  status: LinkedInConnectionStatus | null;
  isLoading: boolean;
  onConnect: () => void;
  onReconnect: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  userId?: string; // Optional user ID for generating reconnect link
  hasUnipileId?: boolean; // Whether the user currently has a Unipile ID
}

export const LinkedInConnection: React.FC<LinkedInConnectionProps> = ({
  status,
  isLoading,
  onConnect,
  onReconnect,
  onRefresh,
  isRefreshing = false,
  userId,
  hasUnipileId,
}) => {
  const handleConnect = useCallback(() => {
    onConnect();
  }, [onConnect]);

  const handleReconnect = useCallback(() => {
    onReconnect();
  }, [onReconnect]);

  const handleRefresh = useCallback(() => {
    if (!onRefresh || isRefreshing) {
      return;
    }
    onRefresh();
  }, [onRefresh, isRefreshing]);

  const [copySuccess, setCopySuccess] = useState(false);
  const handleCopyLink = useCallback(() => {
    // When the user's Unipile ID is empty, use the generic reconnect link (no user_id).
    const baseUrl = `${window.location.origin}/reconnect-linkedin`;
    const reconnectUrl =
      hasUnipileId && userId ? `${baseUrl}/${userId}` : baseUrl;

    navigator.clipboard
      .writeText(reconnectUrl)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy link:', err);
      });
  }, [userId, hasUnipileId]);

  const handleConnectNewAccount = useCallback(() => {
    const baseUrl = `${window.location.origin}/reconnect-linkedin`;
    window.open(baseUrl, '_blank');
  }, []);

  const formatPremiumFeature = useCallback((feature: string): string => {
    const featureMap: Record<string, string> = {
      sales_navigator: 'Sales Navigator',
      recruiter: 'Recruiter',
      premium: 'Premium',
    };
    return featureMap[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, []);

  const getPremiumFeatureColor = useCallback((feature: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    const colorMap: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
      sales_navigator: 'primary',
      recruiter: 'secondary',
      premium: 'success',
    };
    return colorMap[feature] || 'default';
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Checking LinkedIn connection...
        </Typography>
      </Box>
    );
  }

  if (!status) {
    return (
      <Alert severity="error">
        Failed to load LinkedIn connection status
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        opacity: isRefreshing ? 0.6 : 1,
        transition: 'opacity 0.2s ease-out',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        LinkedIn Connection
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LinkedInIcon color="primary" />

        {status.is_connected ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Connected"
            color="success"
            variant="outlined"
          />
        ) : status.has_account ? (
          <Chip
            icon={<ErrorIcon />}
            label="Disconnected"
            color="error"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<LinkIcon />}
            label="Not Connected"
            color="default"
            variant="outlined"
          />
        )}
      </Box>

      {status.is_connected ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Your LinkedIn account is connected and syncing messages.
          </Typography>
          {status.premium_features && status.premium_features.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mt: 1,
                opacity: isRefreshing ? 0.6 : 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Premium Features:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {status.premium_features.map((feature) => (
                  <Chip
                    key={feature}
                    label={formatPremiumFeature(feature)}
                    color={getPremiumFeatureColor(feature)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
          {onRefresh && (
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Refresh from LinkedIn">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        ) : status.has_account ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Your LinkedIn account was disconnected. Click reconnect to restore the connection.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleReconnect}
              sx={{ alignSelf: 'flex-start' }}
            >
              Reconnect LinkedIn
            </Button>
            {/* When user has a Unipile ID but is disconnected, allow connecting a NEW account via the generic link. */}
            {hasUnipileId ? (
              <Tooltip title="Create a brand new LinkedIn connection with a new Unipile ID (useful if the previous account was removed from Unipile).">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<LinkedInIcon />}
                    onClick={handleConnectNewAccount}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Connect New LinkedIn Account
                  </Button>
                </span>
              </Tooltip>
            ) : (
              userId && (
                <Tooltip title={copySuccess ? 'Link copied!' : 'Copy reconnect link to clipboard'}>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyLink}
                    sx={{ alignSelf: 'flex-start' }}
                    color={copySuccess ? 'success' : 'primary'}
                  >
                    {copySuccess ? 'Copied!' : 'Copy Link'}
                  </Button>
                </Tooltip>
              )
            )}
          </Box>
          {onRefresh && (
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Refresh status">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Connect your LinkedIn account to sync messages and manage conversations.
          </Typography>
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={handleConnect}
            sx={{ alignSelf: 'flex-start' }}
          >
            Connect LinkedIn
          </Button>
        </Box>
      )}
    </Box>
  );
}; 